// @flow

import { print } from 'graphql/language/printer';

import { mark } from '@ta/platform.performance';

export type RequestContext = {|
  loggedInUserId?: ?string,
  locale: string,
  uniqueId: string,
  debugToolEnabled: boolean,
  blacklistedServiceCalls: ?(string[]),
|};

// Both Urql and Apollo adhere to this contract
export type Operation = {|
  variables: { [string]: any },
  query: any,
  operationName: ?string,
|};

const makeGraphQlCall = (requestContext: RequestContext, operations: Operation[]): Promise<any> => {
  // eslint-disable-next-line ta-code-conventions/lithium-string-blacklist
  if (!SERVER) return Promise.reject();

  if (!operations.length) {
    return Promise.resolve();
  }

  const start = Date.now();
  if (DEBUG) {
    console.debug('Executing GraphQL batch of size ', operations.length);
  }
  mark('gqlStart');

  // Use the global provided by Node to call the GraphQL service
  // TODO: change this to use fetch
  return microservices
    .graphqlBatched({
      requests: operations.map(operation => ({
        variables: operation.variables,
        query: print(operation.query),
        operationName: operation.operationName,
      })),
      requestContext,
    })
    .then(
      response => {
        if (typeof response === 'undefined') {
          const elapsedTime = Date.now() - start;
          mark('gqlUndef');
          return Promise.reject(
            new Error(`Undefined response from GraphQL after ${elapsedTime} ms`),
          );
        }
        const parsedResponse = JSON.parse(response);
        if (!parsedResponse.result && parsedResponse.message !== undefined) {
          console.warn(
            `GraphQL error code ${parsedResponse.code} after ${Date.now() - start} ms: ${
              parsedResponse.message
            }`,
          );
          // This response comes from the egress proxy
          return Promise.reject(
            new Error(`GraphQL error code ${parsedResponse.code}: ${parsedResponse.message}`),
          );
        }
        mark('gqlEnd');
        return [
          parsedResponse.results.map(result => result.result),
          parsedResponse.results?.[0]?.result?.extensions,
        ];
      },
      err => {
        console.error(`Error fetching GraphQL data after ${Date.now() - start} ms: `, err);
        mark('gqlErr');
        return Promise.reject(err);
      },
    );
};

export default makeGraphQlCall;
